import BooleanFilterDefinition from 'vcomponents/listing/apiListing/filtersDefinition/BooleanFilterDefinition';
import ColumnDefinition from '../ColumnDefinition.js';
import DateIntervalDefinition from '../filtersDefinition/DateIntervalDefinition.js';
import KpiDefinition from 'vcomponents/listing/apiListing/KpiDefinition';
import moment from 'moment';
import SelectFilterDefinition from 'vcomponents/listing/apiListing/filtersDefinition/SelectFilterDefinition';

const { format, apply, parse } = require('@sellsy/numberformat');

const formatAmount = (amount) => {
    const corpNumberFormat =
        window.sellsyapp.config.globalProperties.$store.state['corp']['numformat'];

    const numberFormatConfig = {
        currency: '',
        currencypos: corpNumberFormat.currencypos,
        decimals: corpNumberFormat.decimals,
        thousands: corpNumberFormat.thousands,
        precision: corpNumberFormat.precision,
    };

    if (amount === 0) {
        return format(0, numberFormatConfig);
    }

    const amountParsed = parse(amount);
    return format(amountParsed, numberFormatConfig);
};

export default {
    columns: [
        new ColumnDefinition('type', i18n.__('Code journal')),
        new ColumnDefinition(
            'date',
            i18n.__('Date'),
            false,
            false,
            function (date) {
                return {
                    type: 'component',
                    name: 'date-no-time-value',
                    params: {
                        value: new moment(date).unix(),
                    },
                };
            },
            {
                display: true,
            },
        ),
        new ColumnDefinition(
            'code.value',
            i18n.__('Code comptable'),
            false,
            false,
            function (code) {
                return code.value === undefined ? '' : code.value;
            },
            {
                prop: 'code',
                display: true,
            },
        ),
        new ColumnDefinition(
            'code.label',
            i18n.__('Label code comptable'),
            false,
            false,
            function (code) {
                return code.label === undefined ? '' : code.label;
            },
            {
                prop: 'code',
                display: true,
            },
        ),
        new ColumnDefinition('auxiliary_code', i18n.__('Compte auxiliaire'), false, false, null, {
            display: true,
        }),
        new ColumnDefinition('label', i18n.__('Libellé'), false, false, null, {
            display: true,
        }),
        new ColumnDefinition(
            'company-individual.name',
            i18n.__('Clients') + '/' + i18n.__('Fournisseurs'),
            false,
            false,
            function (formatted, row) {
                const thirdsNameMap = new Map();

                if (row.companies !== null) {
                    row.companies.forEach((company) => {
                        thirdsNameMap.set(company.id, company.name);
                    });
                }

                if (row.individuals !== null) {
                    row.individuals.forEach((individual) => {
                        thirdsNameMap.set(individual.id, individual.name);
                    });
                }
                // values needs to be reordered by id
                return [...thirdsNameMap.entries()]
                    .sort((a, b) => a[0] - b[0])
                    .map((value) => value[1])
                    .join(', ');
            },
            {
                display: true,
            },
        ),
        new ColumnDefinition(
            'debit',
            i18n.__('Débit'),
            false,
            false,
            function (amount, row) {
                if (amount.direction !== undefined && amount.direction === 'debit') {
                    return formatAmount(amount.value === undefined ? '0' : amount.value);
                }

                return formatAmount('0');
            },
            {
                prop: 'amount',
                display: true,
            },
        ),
        new ColumnDefinition(
            'credit',
            i18n.__('Crédit'),
            false,
            false,
            function (amount, row) {
                if (amount.direction !== undefined && amount.direction === 'credit') {
                    return formatAmount(amount.value === undefined ? '0' : amount.value);
                }

                return formatAmount('0');
            },
            {
                prop: 'amount',
                display: true,
            },
        ),
        new ColumnDefinition(
            'amount.value',
            i18n.__('Montant'),
            false,
            false,
            function (amount, row) {
                return formatAmount(amount.value === undefined ? '0' : amount.value);
            },
            {
                prop: 'amount',
                display: false,
            },
        ),
        new ColumnDefinition(
            'document.currency',
            i18n.__('Devise'),
            false,
            false,
            function (document) {
                return document.currency === undefined ? '' : document.currency;
            },
            {
                prop: 'document',
                display: true,
            },
        ),
        new ColumnDefinition(
            'document.number',
            i18n.__('N° Facture'),
            false,
            false,
            function (document) {
                if (document.type && document.id && document.number) {
                    return {
                        type: 'component',
                        name: 'object-link-value',
                        params: {
                            className: 'black-link',
                            object: {
                                type: document.type,
                                id: document.id,
                            },
                            content: document.number,
                        },
                    };
                }
                return document.number === undefined ? '' : document.number;
            },
            {
                prop: 'document',
                display: true,
            },
        ),
        new ColumnDefinition(
            'document.subject',
            i18n.__('Objet'),
            false,
            false,
            function (document) {
                return document.subject === undefined ? '' : document.subject;
            },
            {
                prop: 'document',
                display: true,
            },
        ),
        new ColumnDefinition(
            'recorded',
            i18n.__('Exporté'),
            false,
            false,
            function (recorded) {
                return recorded ? 'Y' : 'N';
            },
            {
                exportable: false,
                display: true,
            },
        ),
        new ColumnDefinition('analytic_code', i18n.__('Code analytique'), false, false, null, {
            display: false,
        }),
        new ColumnDefinition(
            'document.due_date',
            i18n.__('Date d‘échéance'),
            false,
            false,
            function (document) {
                return {
                    type: 'component',
                    name: 'date-value',
                    params: {
                        value: !document.due_date ? 0 : new moment(document.due_date).unix(),
                    },
                };
            },
            {
                prop: 'document',
                display: false,
            },
        ),
        new ColumnDefinition(
            'document.service_start_date',
            i18n.__('Date de début de service'),
            false,
            false,
            function (document) {
                return {
                    type: 'component',
                    name: 'date-value',
                    params: {
                        value: !document.service_start_date
                            ? 0
                            : new moment(document.service_start_date).unix(),
                    },
                };
            },
            {
                prop: 'document',
                display: false,
            },
        ),
        new ColumnDefinition(
            'document.service_end_date',
            i18n.__('Date de fin de service'),
            false,
            false,
            function (document) {
                return {
                    type: 'component',
                    name: 'date-value',
                    params: {
                        value: !document.service_end_date
                            ? 0
                            : new moment(document.service_end_date).unix(),
                    },
                };
            },
            {
                prop: 'document',
                display: false,
            },
        ),
        new ColumnDefinition(
            'document.public_link',
            i18n.__('Lien public'),
            false,
            false,
            function (document) {
                return document.public_link === undefined ? '' : document.public_link;
            },
            {
                prop: 'document',
                display: false,
            },
        ),

        new ColumnDefinition(
            'company-individual.reference',
            i18n.__('Référence client'),
            false,
            false,
            function (formatted, row) {
                const thirdsReference = [];

                if (row.companies !== null) {
                    row.companies.forEach((company) => {
                        thirdsReference.push(company.reference);
                    });
                }

                if (row.individuals !== null) {
                    row.individuals.forEach((individual) => {
                        thirdsReference.push(individual.reference);
                    });
                }

                return thirdsReference.join(', ');
            },
            {
                display: false,
            },
        ),
        new ColumnDefinition(
            'amount.direction',
            i18n.__('Sens (Débit/Crédit)'),
            false,
            false,
            function (amount) {
                return amount.direction === undefined ? '' : amount.direction;
            },
            {
                prop: 'amount',
                display: false,
            },
        ),
        new ColumnDefinition('accounting_type', i18n.__('Type écriture'), false, false, null, {
            display: false,
        }),
        new ColumnDefinition(
            'document.external_reference',
            i18n.__('Référence externe'),
            false,
            false,
            function (document) {
                return document.external_reference === undefined ? '' : document.external_reference;
            },
            {
                prop: 'document',
                display: false,
            },
        ),
        new ColumnDefinition('analytic_plan', i18n.__('N° plan analytique'), false, false, null, {
            display: false,
        }),
    ],
    filters: [
        new DateIntervalDefinition('date', i18n.__('Date'), false, false),
        new SelectFilterDefinition(
            'accounting_codes',
            i18n.__('Codes comptables'),
            function () {
                return this.metas.codes;
            },
            true,
        ),
        new SelectFilterDefinition('currency', i18n.__('Devise'), function () {
            return this.metas.currencies;
        }),
        new BooleanFilterDefinition(
            'include_exported_entries',
            i18n.__('Inclure les lignes déjà exportées'),
        ),
    ],
    hooks: {
        metas: function (metas) {
            return metas;
        },
    },
    contextMenus: [],
    batchActions: [],
    kpis: [
        new KpiDefinition('credit_debit.debit', 'credit_debit', i18n.__('Total débit'), function (
            kpis,
        ) {
            return formatAmount(kpis.debit);
        }),
        new KpiDefinition('credit_debit.credit', 'credit_debit', i18n.__('Total crédit'), function (
            kpis,
        ) {
            return formatAmount(kpis.credit);
        }),
    ],
};
